<script setup>
import { computed, ref, reactive, nextTick, onMounted, onUnmounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import SettingsHeader from 'dashboard/components-next/aifocus/pageComponents/settings/SettingsHeader.vue';

const props = defineProps({
  agentId: {
    type: Number,
    required: true,
  },
});

const store = useStore();

// Generate a unique session ID for this playground session
const generateSessionId = () =>
  `playground-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
const sessionId = ref(generateSessionId());

const agent = computed(() =>
  store.getters['aifocusAgents/getRecord'](props.agentId)
);

const messageInput = ref('');
const isSending = ref(false);
const isStreaming = ref(false);
const messagesContainer = ref(null);
const fileInput = ref(null);
const selectedFiles = ref([]);
const streamingContent = ref('');

const messages = reactive([]);

// File upload handling - Tipos multimodais suportados pelo Agno
const supportedFileTypes = [
  // Imagens
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
  // Audio (transcrito via Whisper)
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/ogg',
  'audio/webm',
  'audio/m4a',
  'audio/aac',
  'audio/flac',
  // Video (frame extraction)
  'video/mp4',
  'video/webm',
  'video/ogg',
  'video/quicktime',
  // Documentos (texto extraido)
  'application/pdf',
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const maxFileSize = 20 * 1024 * 1024; // 20MB

onMounted(() => {
  if (!agent.value) {
    store.dispatch('aifocusAgents/show', props.agentId);
  }
});

onUnmounted(() => {
  // Cleanup any ongoing streams
  isStreaming.value = false;
});

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
};

const handleFileSelect = event => {
  const files = Array.from(event.target.files);
  const validFiles = files.filter(file => {
    if (!supportedFileTypes.includes(file.type)) {
      useAlert(`Tipo de arquivo nao suportado: ${file.type}`);
      return false;
    }
    if (file.size > maxFileSize) {
      useAlert(`Arquivo muito grande: ${file.name} (max 20MB)`);
      return false;
    }
    return true;
  });

  selectedFiles.value = [...selectedFiles.value, ...validFiles].slice(0, 5); // Max 5 files
  event.target.value = ''; // Reset input
};

const removeFile = index => {
  selectedFiles.value.splice(index, 1);
};

const getFileIcon = file => {
  if (file.type.startsWith('image/')) return 'i-lucide-image';
  if (file.type.startsWith('audio/')) return 'i-lucide-music';
  if (file.type.startsWith('video/')) return 'i-lucide-video';
  if (file.type === 'application/pdf') return 'i-lucide-file-text';
  if (file.type.includes('word') || file.type === 'application/msword')
    return 'i-lucide-file-text';
  if (file.type.includes('excel') || file.type === 'application/vnd.ms-excel')
    return 'i-lucide-file-spreadsheet';
  if (file.type.startsWith('text/')) return 'i-lucide-file-text';
  return 'i-lucide-file';
};

const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const encodeFileToBase64 = file => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const handleSend = async () => {
  const content = messageInput.value.trim();
  if ((!content && selectedFiles.value.length === 0) || isSending.value) return;

  // Add user message with file previews
  const userMessage = {
    id: Date.now(),
    role: 'user',
    content: content || 'Enviou arquivo(s)',
    timestamp: new Date(),
    files: selectedFiles.value.map(f => ({
      name: f.name,
      type: f.type,
      size: f.size,
    })),
  };
  messages.push(userMessage);

  const filesToSend = [...selectedFiles.value];
  messageInput.value = '';
  selectedFiles.value = [];
  scrollToBottom();

  try {
    isSending.value = true;

    // Encode files to base64
    const encodedFiles = await Promise.all(
      filesToSend.map(async file => ({
        filename: file.name,
        content_type: file.type,
        base64: await encodeFileToBase64(file),
      }))
    );

    const response = await store.dispatch('aifocusAgents/playground', {
      agentId: props.agentId,
      messageContent: content,
      sessionId: sessionId.value,
      files: encodedFiles.length > 0 ? encodedFiles : undefined,
      messageHistory: messages
        .filter(m => m.role !== 'system')
        .slice(-10)
        .map(m => ({ role: m.role, content: m.content })),
    });

    messages.push({
      id: Date.now() + 1,
      role: 'assistant',
      content: response.response || response.content || 'Sem resposta',
      timestamp: new Date(),
    });
    scrollToBottom();
  } catch (error) {
    useAlert(error?.message || 'Erro ao enviar mensagem');
    messages.push({
      id: Date.now() + 1,
      role: 'system',
      content: `Erro: ${error?.message || 'Falha ao comunicar com o agente'}`,
      timestamp: new Date(),
      isError: true,
    });
    scrollToBottom();
  } finally {
    isSending.value = false;
  }
};

const handleKeyDown = event => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault();
    handleSend();
  }
};

const handleClearChat = () => {
  messages.splice(0, messages.length);
  selectedFiles.value = [];
  sessionId.value = generateSessionId();
};

const formatTime = date => {
  return new Date(date).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isSynced = computed(() => !!agent.value?.external_agent_id);

// Model info display
const modelInfo = computed(() => {
  if (!agent.value) return null;
  return {
    provider: agent.value.provider || 'openai',
    model: agent.value.model || 'gpt-4o-mini',
  };
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<!-- eslint-disable @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="flex flex-col h-[calc(100vh-280px)] min-h-[500px]">
    <!-- Header -->
    <div class="mb-4">
      <div class="flex items-center justify-between">
        <SettingsHeader
          heading="Playground"
          description="Teste seu agente com mensagens, imagens, audio e documentos."
        />
        <Button
          v-if="messages.length > 0"
          label="Limpar Chat"
          variant="secondary"
          size="sm"
          icon="i-lucide-trash-2"
          @click="handleClearChat"
        />
      </div>

      <div
        v-if="agent"
        class="mt-4 p-3 bg-n-alpha-1 rounded-lg flex items-center gap-3"
      >
        <div
          class="w-10 h-10 rounded-full bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-bot text-xl text-n-slate-11" />
        </div>
        <div class="flex-1">
          <p class="font-medium text-n-slate-12">{{ agent.name }}</p>
          <p class="text-xs text-n-slate-10">
            {{ modelInfo?.provider }} / {{ modelInfo?.model }}
          </p>
        </div>
        <!-- Capability badges -->
        <div class="flex gap-1">
          <span
            class="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700"
            title="Suporta imagens"
          >
            Vision
          </span>
          <span
            class="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-700"
            title="Memoria persistente"
          >
            Memory
          </span>
        </div>
        <span
          v-if="isSynced"
          class="px-2 py-1 text-xs font-medium rounded-full bg-green-500/10 text-green-500"
        >
          Sincronizado
        </span>
        <span
          v-else
          class="px-2 py-1 text-xs font-medium rounded-full bg-yellow-500/10 text-yellow-500"
        >
          Nao sincronizado
        </span>
      </div>
    </div>

    <!-- Messages -->
    <div ref="messagesContainer" class="flex-1 overflow-y-auto">
      <div
        v-if="messages.length === 0"
        class="flex flex-col items-center justify-center h-full text-center"
      >
        <div
          class="w-16 h-16 mb-4 rounded-full bg-n-alpha-2 flex items-center justify-center"
        >
          <span class="i-lucide-message-square text-3xl text-n-slate-10" />
        </div>
        <h3 class="text-lg font-medium text-n-slate-12 mb-2">
          Comece uma conversa
        </h3>
        <p class="text-sm text-n-slate-11 max-w-md mb-4">
          Envie uma mensagem ou anexe arquivos para testar seu agente.
        </p>
        <div
          class="flex flex-wrap gap-2 justify-center text-xs text-n-slate-10"
        >
          <span class="px-2 py-1 bg-n-alpha-1 rounded">Imagens</span>
          <span class="px-2 py-1 bg-n-alpha-1 rounded">Audio</span>
          <span class="px-2 py-1 bg-n-alpha-1 rounded">Video</span>
          <span class="px-2 py-1 bg-n-alpha-1 rounded">PDF</span>
        </div>
      </div>

      <div v-else class="space-y-4 pb-4">
        <div
          v-for="message in messages"
          :key="message.id"
          class="flex gap-3"
          :class="message.role === 'user' ? 'justify-end' : 'justify-start'"
        >
          <div
            v-if="message.role !== 'user'"
            class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
            :class="message.isError ? 'bg-ruby-500/10' : 'bg-n-alpha-2'"
          >
            <span
              class="text-lg"
              :class="
                message.isError
                  ? 'i-lucide-alert-circle text-ruby-500'
                  : 'i-lucide-bot text-n-slate-11'
              "
            />
          </div>

          <div
            class="max-w-[70%] rounded-2xl px-4 py-2"
            :class="
              message.role === 'user'
                ? 'bg-n-brand text-white rounded-br-sm'
                : message.isError
                  ? 'bg-ruby-500/10 text-ruby-500 rounded-bl-sm'
                  : 'bg-n-alpha-2 text-n-slate-12 rounded-bl-sm'
            "
          >
            <!-- File attachments preview -->
            <div
              v-if="message.files && message.files.length > 0"
              class="flex flex-wrap gap-2 mb-2"
            >
              <div
                v-for="(file, idx) in message.files"
                :key="idx"
                class="flex items-center gap-1 px-2 py-1 rounded text-xs"
                :class="
                  message.role === 'user' ? 'bg-white/20' : 'bg-n-alpha-1'
                "
              >
                <span :class="getFileIcon(file)" />
                <span class="truncate max-w-[100px]">{{ file.name }}</span>
              </div>
            </div>
            <p class="text-sm whitespace-pre-wrap">{{ message.content }}</p>
            <p
              class="text-xs mt-1 opacity-70"
              :class="message.role === 'user' ? 'text-right' : ''"
            >
              {{ formatTime(message.timestamp) }}
            </p>
          </div>

          <div
            v-if="message.role === 'user'"
            class="flex-shrink-0 w-8 h-8 rounded-full bg-n-brand/20 flex items-center justify-center"
          >
            <span class="i-lucide-user text-lg text-n-brand" />
          </div>
        </div>

        <!-- Streaming/Loading indicator -->
        <div v-if="isSending || isStreaming" class="flex gap-3">
          <div
            class="w-8 h-8 rounded-full bg-n-alpha-2 flex items-center justify-center"
          >
            <span class="i-lucide-bot text-lg text-n-slate-11" />
          </div>
          <div class="bg-n-alpha-2 rounded-2xl rounded-bl-sm px-4 py-3">
            <div v-if="streamingContent" class="text-sm whitespace-pre-wrap">
              {{ streamingContent }}
            </div>
            <div v-else class="flex gap-1">
              <span class="w-2 h-2 bg-n-slate-10 rounded-full animate-bounce" />
              <span
                class="w-2 h-2 bg-n-slate-10 rounded-full animate-bounce animation-delay-100"
              />
              <span
                class="w-2 h-2 bg-n-slate-10 rounded-full animate-bounce animation-delay-200"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Input Area -->
    <div class="pt-4 border-t border-n-weak">
      <div
        v-if="!isSynced"
        class="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg"
      >
        <p class="text-sm text-yellow-600">
          Este agente ainda nao foi sincronizado. Volte para a tab
          "Configuracoes" e clique em "Sincronizar".
        </p>
      </div>

      <!-- Selected files preview -->
      <div v-if="selectedFiles.length > 0" class="mb-3 flex flex-wrap gap-2">
        <div
          v-for="(file, index) in selectedFiles"
          :key="index"
          class="flex items-center gap-2 px-3 py-1.5 bg-n-alpha-1 rounded-lg text-sm"
        >
          <span :class="getFileIcon(file)" class="text-n-slate-11" />
          <span class="truncate max-w-[150px]">{{ file.name }}</span>
          <span class="text-xs text-n-slate-10">
            {{ formatFileSize(file.size) }}
          </span>
          <button
            class="text-n-slate-10 hover:text-ruby-500 transition-colors"
            @click="removeFile(index)"
          >
            <span class="i-lucide-x text-sm" />
          </button>
        </div>
      </div>

      <div class="flex gap-3 items-end">
        <!-- File upload button -->
        <input
          ref="fileInput"
          type="file"
          multiple
          :accept="supportedFileTypes.join(',')"
          class="hidden"
          @change="handleFileSelect"
        />
        <Button
          icon="i-lucide-paperclip"
          variant="ghost"
          size="sm"
          :disabled="!isSynced || isSending || selectedFiles.length >= 5"
          title="Anexar arquivo (max 5)"
          @click="fileInput?.click()"
        />

        <textarea
          v-model="messageInput"
          rows="1"
          class="flex-1 px-4 py-3 bg-n-alpha-1 border border-n-weak rounded-xl text-n-slate-12 resize-none focus:outline-none focus:ring-2 focus:ring-n-brand/50"
          placeholder="Digite sua mensagem..."
          :disabled="!isSynced || isSending"
          @keydown="handleKeyDown"
        />
        <Button
          icon="i-lucide-send"
          :disabled="
            (!messageInput.trim() && selectedFiles.length === 0) ||
            !isSynced ||
            isSending
          "
          :is-loading="isSending"
          @click="handleSend"
        />
      </div>
      <p class="text-xs text-n-slate-10 mt-2 text-center">
        Suporta imagens, audio, video, PDF, DOCX, Excel e texto
      </p>
    </div>
  </div>
</template>

<style scoped>
.animation-delay-100 {
  animation-delay: 0.1s;
}
.animation-delay-200 {
  animation-delay: 0.2s;
}
</style>
